import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';
import type {
  Channel,

  Message,
  User,
  ConnectionState,
  DmChannel,
  PendingMessage,
} from '../types';
import type { PermissionDetail } from '../lib/api';
import * as api from '../lib/api';

export interface IntentionalLogoutState {
  begin: () => void;
  cancel: () => void;
  shouldAnnounceSessionEnded: () => boolean;
}

export function createIntentionalLogoutState(): IntentionalLogoutState {
  let intentional = false;
  return {
    begin: () => {
      intentional = true;
    },
    cancel: () => {
      intentional = false;
    },
    shouldAnnounceSessionEnded: () => !intentional,
  };
}

// ─── State ──────────────────────────────────────────────

interface AppState {
  guildId: string | null;
  channels: Channel[];
  dmChannels: DmChannel[];
  currentChannelId: string | null;
  messages: Map<string, Message[]>; // channelId -> messages
  hasMore: Map<string, boolean>; // channelId -> has_more
  loadingMessages: Set<string>; // channelIds currently loading
  currentUser: User | null;
  permissions: PermissionDetail[] | null;
  onlineUserIds: Set<string>;
  connectionState: ConnectionState;
  channelMembersVersion: Map<string, number>; // channelId -> version counter
  typingUsers: Map<string, Map<string, { displayName: string; expiresAt: number }>>;
  pendingMessages: Map<string, PendingMessage[]>;
  initialized: boolean;
}

// gh#686 §6: 这两个导出供 reducer-race 单测构造 race 路径用.
export type { AppState };
export const initialState: AppState = {
  guildId: null,
  channels: [],
  dmChannels: [],
  currentChannelId: null,
  messages: new Map(),
  hasMore: new Map(),
  loadingMessages: new Set(),
  currentUser: null,
  permissions: null,
  onlineUserIds: new Set(),
  connectionState: 'disconnected',
  channelMembersVersion: new Map(),
  typingUsers: new Map(),
  pendingMessages: new Map(),
  initialized: false,
};

// ─── Actions ────────────────────────────────────────────

type Action =
  | { type: 'SET_CHANNELS'; channels: Channel[] }
  | { type: 'ADD_CHANNEL'; channel: Channel }
  | { type: 'SET_CURRENT_CHANNEL'; channelId: string | null }
  | {
      type: 'SET_MESSAGES';
      channelId: string;
      messages: Message[];
      hasMore: boolean;
      loadedBefore?: number;
    }
  | { type: 'PREPEND_MESSAGES'; channelId: string; messages: Message[]; hasMore: boolean }
  | { type: 'ADD_MESSAGE'; channelId: string; message: Message }
  | { type: 'SET_LOADING_MESSAGES'; channelId: string; loading: boolean }
  | { type: 'SET_CURRENT_USER'; user: User | null }
  | { type: 'SET_PERMISSIONS'; permissions: PermissionDetail[] | null }
  | { type: 'SET_ONLINE_USERS'; userIds: string[] }
  | { type: 'USER_ONLINE'; userId: string }
  | { type: 'USER_OFFLINE'; userId: string }
  | { type: 'SET_CONNECTION_STATE'; state: ConnectionState }
  | { type: 'SET_INITIALIZED' }
  | { type: 'SET_GUILD_ID'; guildId: string }
  | { type: 'UPDATE_UNREAD'; channelId: string; count: number }
  | { type: 'CLEAR_UNREAD'; channelId: string }
  | { type: 'SET_DM_CHANNELS'; channels: DmChannel[] }
  | { type: 'ADD_DM_CHANNEL'; channel: DmChannel }
  | { type: 'UPDATE_DM_CHANNEL'; channelId: string; updates: Partial<DmChannel> }
  | { type: 'REMOVE_CHANNEL'; channelId: string }
  | { type: 'UPDATE_CHANNEL'; channelId: string; updates: Partial<Channel> }
  | { type: 'BUMP_CHANNEL_MEMBERS_VERSION'; channelId: string }
  | { type: 'SET_TYPING'; channelId: string; userId: string; displayName: string }
  | { type: 'CLEAR_EXPIRED_TYPING' }
  | {
      type: 'UPDATE_REACTIONS';
      messageId: string;
      channelId: string;
      reactions: { emoji: string; count: number; user_ids: string[] }[];
    }
  | {
      type: 'ADD_REACTION_OPTIMISTIC';
      channelId: string;
      messageId: string;
      emoji: string;
      userId: string;
    }
  | {
      type: 'REMOVE_REACTION_OPTIMISTIC';
      channelId: string;
      messageId: string;
      emoji: string;
      userId: string;
    }
  | { type: 'ADD_PENDING_MESSAGE'; message: PendingMessage }
  | {
      type: 'ACK_PENDING_MESSAGE';
      clientMessageId: string;
      channelId: string;
      serverMessage: Message;
    }
  | { type: 'FAIL_PENDING_MESSAGE'; clientMessageId: string; channelId: string }
  | { type: 'REMOVE_PENDING_MESSAGE'; clientMessageId: string; channelId: string }
  | { type: 'CLEAR_LOCAL_MESSAGES'; payload: { channelId: string } }
  | { type: 'INSERT_LOCAL_SYSTEM_MESSAGE'; payload: { channelId: string; text: string } }
  | { type: 'NAVIGATE_AFTER_LEAVE'; payload: { channelId: string } }
  | {
      type: 'EDIT_MESSAGE';
      channelId: string;
      messageId: string;
      content: string;
      editedAt: number;
    }
  | { type: 'DELETE_MESSAGE'; channelId: string; messageId: string; deletedAt: number }
  | { type: 'RESET_AUTH_STATE' };

// gh#686 §6: reducer 导出供 reducer-race 单测直接 dispatch 不必包 Provider.
export function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'RESET_AUTH_STATE':
      return {
        ...initialState,
        messages: new Map(),
        hasMore: new Map(),
        loadingMessages: new Set(),
        onlineUserIds: new Set(),
        channelMembersVersion: new Map(),
        typingUsers: new Map(),
        pendingMessages: new Map(),
      };
    case 'SET_CHANNELS':
      return { ...state, channels: action.channels };

    case 'ADD_CHANNEL': {
      // Avoid duplicates. CHN-1.3 hardening: guard against malformed WS
      // payload (e.g., legacy `channel_added` carrying only channel_id) so
      // the reducer never derefs undefined and crashes AppProvider.
      if (!action.channel || !action.channel.id) return state;
      if (state.channels.some((c) => c.id === action.channel.id)) return state;
      return { ...state, channels: [...state.channels, action.channel] };
    }

    case 'SET_CURRENT_CHANNEL':
      return { ...state, currentChannelId: action.channelId };

    case 'SET_MESSAGES': {
      const msgs = new Map(state.messages);
      const hm = new Map(state.hasMore);
      const incomingIds = new Set(action.messages.map((m) => m.id));
      const newestIncomingAt = action.messages.reduce(
        (max, m) => Math.max(max, m.created_at),
        Number.NEGATIVE_INFINITY,
      );
      const liveCutoff =
        action.loadedBefore ??
        (action.messages.length > 0 ? newestIncomingAt : Number.POSITIVE_INFINITY);
      const newerLiveMessages = (state.messages.get(action.channelId) ?? []).filter(
        (m) => !incomingIds.has(m.id) && !m.deleted_at && m.created_at > liveCutoff,
      );
      const merged = [...action.messages, ...newerLiveMessages].sort(
        (a, b) => a.created_at - b.created_at,
      );
      msgs.set(action.channelId, merged);
      hm.set(action.channelId, action.hasMore);
      return { ...state, messages: msgs, hasMore: hm };
    }

    case 'PREPEND_MESSAGES': {
      const msgs = new Map(state.messages);
      const hm = new Map(state.hasMore);
      const existing = msgs.get(action.channelId) ?? [];
      // Deduplicate
      const existingIds = new Set(existing.map((m) => m.id));
      const newMsgs = action.messages.filter((m) => !existingIds.has(m.id));
      msgs.set(action.channelId, [...newMsgs, ...existing]);
      hm.set(action.channelId, action.hasMore);
      return { ...state, messages: msgs, hasMore: hm };
    }

    case 'ADD_MESSAGE': {
      const msgs = new Map(state.messages);
      const existing = msgs.get(action.channelId) ?? [];
      // Deduplicate
      if (existing.some((m) => m.id === action.message.id)) return state;
      msgs.set(action.channelId, [...existing, action.message]);

      // #687 Layer 3 — own message 在 non-current channel 不 bump unread.
      // 多设备场景: 设备 A 发消息, 设备 B (同 user) 收到 ws frame, 如果 B
      // 当前不在该 channel, reducer 不能把 own message 算成未读 (UX flicker).
      // currentUser 可能 null (auth 未完成); optional chain 让对比走 undefined !==
      // sender_id, isOwnMessage = false → 走原行为. 不引入新 crash 路径.
      const isOwnMessage = !!state.currentUser && action.message.sender_id === state.currentUser.id;

      // Reconcile the sender's own optimistic pending bubble the moment its
      // confirmed message arrives — do NOT wait for the message_ack. The server
      // broadcasts new_message before the per-connection ack (same order the
      // REST rail has), and the confirmed message carries no client_message_id,
      // so without this the pending bubble (keyed by clientMessageId, removed
      // only by the ack) and the confirmed bubble briefly coexist → a duplicate
      // for the sender. Drop the first still-'pending' entry in this channel
      // whose content matches; the later ACK_PENDING_MESSAGE then no-ops (its
      // filter finds nothing, and it re-adds a message already deduped by id).
      // This also self-heals a lost ack (the send really succeeded). 'failed'
      // entries are left untouched so a genuine nack keeps its retry UI.
      let pendingMessages = state.pendingMessages;
      if (isOwnMessage) {
        const chPending = state.pendingMessages.get(action.channelId);
        if (chPending && chPending.length > 0) {
          const idx = chPending.findIndex(
            (p) => p.status === 'pending' && p.content === action.message.content,
          );
          if (idx !== -1) {
            const nextList = chPending.slice(0, idx).concat(chPending.slice(idx + 1));
            pendingMessages = new Map(state.pendingMessages);
            if (nextList.length === 0) pendingMessages.delete(action.channelId);
            else pendingMessages.set(action.channelId, nextList);
          }
        }
      }

      // Update channel last_message_at and bump unread if not current channel
      const channels = state.channels.map((c) => {
        if (c.id !== action.channelId) return c;
        return { ...c, last_message_at: action.message.created_at };
      });

      // Increment unread for non-current channel (Layer 3: skip own).
      const updatedChannels = channels.map((c) => {
        if (c.id === action.channelId && c.id !== state.currentChannelId && !isOwnMessage) {
          return { ...c, unread_count: (c.unread_count ?? 0) + 1 };
        }
        return c;
      });

      // Update DM channels too (Layer 3: skip own).
      const dmChannels = state.dmChannels.map((dm) => {
        if (dm.id !== action.channelId) return dm;
        const updated = {
          ...dm,
          last_message: { content: action.message.content, created_at: action.message.created_at },
        };
        if (dm.id !== state.currentChannelId && !isOwnMessage) {
          updated.unread_count = (dm.unread_count ?? 0) + 1;
        }
        return updated;
      });

      // typing-clear-on-message: drop the sender's typing entry the instant
      // their message lands, so the "… 正在输入" bubble clears immediately
      // rather than lingering up to the 3s TTL. Role-agnostic (humans + agents).
      // Guarded + immutable: keep the same state.typingUsers reference when the
      // sender wasn't typing. (ADD_MESSAGE always returns a fresh {...state}, so
      // context consumers re-render anyway; the stable reference is for
      // downstream memoization keyed on state.typingUsers — useMemo/React.memo.)
      let typingUsers = state.typingUsers;
      const chTyping = state.typingUsers.get(action.channelId);
      if (chTyping?.has(action.message.sender_id)) {
        const nextTyping = new Map(chTyping);
        nextTyping.delete(action.message.sender_id);
        typingUsers = new Map(state.typingUsers);
        if (nextTyping.size === 0) typingUsers.delete(action.channelId);
        else typingUsers.set(action.channelId, nextTyping);
      }

      return {
        ...state,
        messages: msgs,
        channels: updatedChannels,
        dmChannels,
        pendingMessages,
        typingUsers,
      };
    }

    case 'SET_LOADING_MESSAGES': {
      const loading = new Set(state.loadingMessages);
      if (action.loading) loading.add(action.channelId);
      else loading.delete(action.channelId);
      return { ...state, loadingMessages: loading };
    }

    case 'SET_CURRENT_USER':
      return { ...state, currentUser: action.user };

    case 'SET_PERMISSIONS':
      return { ...state, permissions: action.permissions };

    case 'SET_ONLINE_USERS':
      return { ...state, onlineUserIds: new Set(action.userIds) };

    case 'USER_ONLINE': {
      const s = new Set(state.onlineUserIds);
      s.add(action.userId);
      return { ...state, onlineUserIds: s };
    }

    case 'USER_OFFLINE': {
      const s = new Set(state.onlineUserIds);
      s.delete(action.userId);
      return { ...state, onlineUserIds: s };
    }

    case 'SET_CONNECTION_STATE':
      return { ...state, connectionState: action.state };

    case 'SET_INITIALIZED':
      return { ...state, initialized: true };

    case 'SET_GUILD_ID':
      return { ...state, guildId: action.guildId };

    case 'UPDATE_UNREAD': {
      const channels = state.channels.map((c) =>
        c.id === action.channelId ? { ...c, unread_count: action.count } : c,
      );
      return { ...state, channels };
    }

    case 'CLEAR_UNREAD': {
      const channels = state.channels.map((c) =>
        c.id === action.channelId ? { ...c, unread_count: 0 } : c,
      );
      const dmChannels = state.dmChannels.map((dm) =>
        dm.id === action.channelId ? { ...dm, unread_count: 0 } : dm,
      );
      return { ...state, channels, dmChannels };
    }

    case 'SET_DM_CHANNELS':
      return { ...state, dmChannels: action.channels };

    case 'ADD_DM_CHANNEL': {
      if (state.dmChannels.some((dm) => dm.id === action.channel.id)) return state;
      return { ...state, dmChannels: [...state.dmChannels, action.channel] };
    }

    case 'UPDATE_DM_CHANNEL': {
      const dmChannels = state.dmChannels.map((dm) =>
        dm.id === action.channelId ? { ...dm, ...action.updates } : dm,
      );
      return { ...state, dmChannels };
    }

    case 'REMOVE_CHANNEL': {
      const channels = state.channels.filter((c) => c.id !== action.channelId);
      const currentChannelId =
        state.currentChannelId === action.channelId
          ? (state.channels.find((c) => c.name === 'general')?.id ?? null)
          : state.currentChannelId;
      return { ...state, channels, currentChannelId };
    }

    case 'UPDATE_CHANNEL': {
      const channels = state.channels.map((c) =>
        c.id === action.channelId ? { ...c, ...action.updates } : c,
      );
      return { ...state, channels };
    }

    case 'BUMP_CHANNEL_MEMBERS_VERSION': {
      const v = new Map(state.channelMembersVersion);
      v.set(action.channelId, (v.get(action.channelId) ?? 0) + 1);
      return { ...state, channelMembersVersion: v };
    }

    case 'SET_TYPING': {
      const typingUsers = new Map(state.typingUsers);
      const channelMap = new Map(typingUsers.get(action.channelId) ?? new Map());
      channelMap.set(action.userId, {
        displayName: action.displayName,
        expiresAt: Date.now() + 3000,
      });
      typingUsers.set(action.channelId, channelMap);
      return { ...state, typingUsers };
    }

    case 'CLEAR_EXPIRED_TYPING': {
      const now = Date.now();
      let changed = false;
      const typingUsers = new Map(state.typingUsers);
      for (const [channelId, userMap] of typingUsers) {
        const filtered = new Map(userMap);
        for (const [userId, info] of filtered) {
          if (info.expiresAt < now) {
            filtered.delete(userId);
            changed = true;
          }
        }
        if (filtered.size === 0) {
          typingUsers.delete(channelId);
        } else {
          typingUsers.set(channelId, filtered);
        }
      }
      return changed ? { ...state, typingUsers } : state;
    }

    case 'UPDATE_REACTIONS': {
      const msgs = new Map(state.messages);
      const channelMsgs = msgs.get(action.channelId);
      if (!channelMsgs) return state;
      const updated = channelMsgs.map((m) =>
        m.id === action.messageId ? { ...m, reactions: action.reactions } : m,
      );
      msgs.set(action.channelId, updated);
      return { ...state, messages: msgs };
    }

    // gh#686 §4 #11/#11a/#11b — 乐观渲染 reaction. WS 推到走
    // UPDATE_REACTIONS 整体替换 (上面那个 case), 这里加的是用户点 ➕ 选 emoji
    // 的瞬间立刻在 UI 显 pill 用的. 这两个 action 跟 UPDATE_REACTIONS 共存
    // 不冲突: 用户点 → ADD_REACTION_OPTIMISTIC 立刻显; 服务器 ack 后 WS 推
    // → UPDATE_REACTIONS 整列替换为服务器版完整 reactions (服务器端按 user_id
    // + emoji dedupe), client 不会出 count=2 重复.
    //
    // ADD: 找到 emoji 那条; 没就新建 {emoji, count: 1, user_ids: [userId]};
    // 有但 user_ids 不含 userId 就 push + count++; 已含 userId 则不动 (幂等).
    case 'ADD_REACTION_OPTIMISTIC': {
      const msgs = new Map(state.messages);
      const channelMsgs = msgs.get(action.channelId);
      if (!channelMsgs) return state;
      const updated = channelMsgs.map((m) => {
        if (m.id !== action.messageId) return m;
        const reactions = m.reactions ?? [];
        const idx = reactions.findIndex((r) => r.emoji === action.emoji);
        if (idx === -1) {
          // 新加一条 emoji
          return {
            ...m,
            reactions: [...reactions, { emoji: action.emoji, count: 1, user_ids: [action.userId] }],
          };
        }
        const existing = reactions[idx]!;
        if (existing.user_ids.includes(action.userId)) {
          // 幂等: 已含当前用户, 不重复加
          return m;
        }
        const next = reactions.slice();
        next[idx] = {
          emoji: existing.emoji,
          count: existing.count + 1,
          user_ids: [...existing.user_ids, action.userId],
        };
        return { ...m, reactions: next };
      });
      msgs.set(action.channelId, updated);
      return { ...state, messages: msgs };
    }

    // REMOVE 关键 (按 feima R2 §4 #11b): race 中 WS 已经把 reactions 整列
    // 替换为服务器版含别人 reaction 时, 不能"按 emoji 删整条"会误删别人.
    // 必须按 user_id 移除当前用户: 找到 emoji 那条, 从 user_ids 移除 userId,
    // count - 1, count == 0 删整条. 跟既有 ReactionBar.handleToggle 走
    // removeReaction API 的 server 端语义同源.
    case 'REMOVE_REACTION_OPTIMISTIC': {
      const msgs = new Map(state.messages);
      const channelMsgs = msgs.get(action.channelId);
      if (!channelMsgs) return state;
      const updated = channelMsgs.map((m) => {
        if (m.id !== action.messageId) return m;
        const reactions = m.reactions ?? [];
        const idx = reactions.findIndex((r) => r.emoji === action.emoji);
        if (idx === -1) return m; // emoji 已不在 (e.g. WS 已收掉), 不动
        const existing = reactions[idx]!;
        if (!existing.user_ids.includes(action.userId)) {
          // 当前用户不在那条 user_ids 里, 不动 (race 中可能 WS 已经清掉)
          return m;
        }
        const remainingUserIds = existing.user_ids.filter((id) => id !== action.userId);
        if (remainingUserIds.length === 0) {
          // 只剩当前用户, 删整条
          return { ...m, reactions: reactions.filter((_, i) => i !== idx) };
        }
        // 还有别人, 只移除当前用户保留别人 reaction
        const next = reactions.slice();
        next[idx] = {
          emoji: existing.emoji,
          count: Math.max(0, existing.count - 1),
          user_ids: remainingUserIds,
        };
        return { ...m, reactions: next };
      });
      msgs.set(action.channelId, updated);
      return { ...state, messages: msgs };
    }

    case 'ADD_PENDING_MESSAGE': {
      const pm = new Map(state.pendingMessages);
      const list = [...(pm.get(action.message.channelId) ?? []), action.message];
      pm.set(action.message.channelId, list);
      return { ...state, pendingMessages: pm };
    }

    case 'ACK_PENDING_MESSAGE': {
      const pm = new Map(state.pendingMessages);
      const list = (pm.get(action.channelId) ?? []).filter(
        (p) => p.clientMessageId !== action.clientMessageId,
      );
      if (list.length === 0) pm.delete(action.channelId);
      else pm.set(action.channelId, list);

      const msgs = new Map(state.messages);
      const existing = msgs.get(action.channelId) ?? [];
      if (!existing.some((m) => m.id === action.serverMessage.id)) {
        msgs.set(action.channelId, [...existing, action.serverMessage]);
      }

      const channels = state.channels.map((c) => {
        if (c.id !== action.channelId) return c;
        return { ...c, last_message_at: action.serverMessage.created_at };
      });

      const dmChannels = state.dmChannels.map((dm) => {
        if (dm.id !== action.channelId) return dm;
        return {
          ...dm,
          last_message: {
            content: action.serverMessage.content,
            created_at: action.serverMessage.created_at,
          },
        };
      });

      return { ...state, pendingMessages: pm, messages: msgs, channels, dmChannels };
    }

    case 'FAIL_PENDING_MESSAGE': {
      const pm = new Map(state.pendingMessages);
      const list = (pm.get(action.channelId) ?? []).map((p) =>
        p.clientMessageId === action.clientMessageId ? { ...p, status: 'failed' as const } : p,
      );
      pm.set(action.channelId, list);
      return { ...state, pendingMessages: pm };
    }

    case 'REMOVE_PENDING_MESSAGE': {
      const pm = new Map(state.pendingMessages);
      const list = (pm.get(action.channelId) ?? []).filter(
        (p) => p.clientMessageId !== action.clientMessageId,
      );
      if (list.length === 0) pm.delete(action.channelId);
      else pm.set(action.channelId, list);
      return { ...state, pendingMessages: pm };
    }

    case 'CLEAR_LOCAL_MESSAGES': {
      const msgs = new Map(state.messages);
      msgs.set(action.payload.channelId, []);
      return { ...state, messages: msgs };
    }

    case 'INSERT_LOCAL_SYSTEM_MESSAGE': {
      const msgs = new Map(state.messages);
      const existing = msgs.get(action.payload.channelId) ?? [];
      const systemMsg: Message = {
        id: `local-${Date.now()}-${Math.random()}`,
        channel_id: action.payload.channelId,
        sender_id: 'system',
        content: action.payload.text,
        content_type: 'text',
        reply_to_id: null,
        created_at: Date.now(),
        edited_at: null,
      };
      msgs.set(action.payload.channelId, [...existing, systemMsg]);
      return { ...state, messages: msgs };
    }

    case 'NAVIGATE_AFTER_LEAVE': {
      const channels = state.channels.filter((c) => c.id !== action.payload.channelId);
      const fallback = channels.find((c) => c.name === 'general')?.id ?? channels[0]?.id ?? null;
      const messages = new Map(state.messages);
      messages.delete(action.payload.channelId);
      const pendingMessages = new Map(state.pendingMessages);
      pendingMessages.delete(action.payload.channelId);
      const typingUsers = new Map(state.typingUsers);
      typingUsers.delete(action.payload.channelId);
      const channelMembersVersion = new Map(state.channelMembersVersion);
      channelMembersVersion.delete(action.payload.channelId);
      return {
        ...state,
        channels,
        currentChannelId: fallback,
        messages,
        pendingMessages,
        typingUsers,
        channelMembersVersion,
      };
    }

    case 'EDIT_MESSAGE': {
      const msgs = new Map(state.messages);
      const channelMsgs = msgs.get(action.channelId);
      if (!channelMsgs) return state;
      const updated = channelMsgs.map((m) =>
        m.id === action.messageId
          ? { ...m, content: action.content, edited_at: action.editedAt }
          : m,
      );
      msgs.set(action.channelId, updated);
      return { ...state, messages: msgs };
    }

    case 'DELETE_MESSAGE': {
      const msgs = new Map(state.messages);
      const channelMsgs = msgs.get(action.channelId);
      if (!channelMsgs) return state;
      const updated = channelMsgs.map((m) =>
        m.id === action.messageId ? { ...m, content: '', deleted_at: action.deletedAt } : m,
      );
      msgs.set(action.channelId, updated);
      return { ...state, messages: msgs };
    }

    default:
      return state;
  }
}

// ─── Context ────────────────────────────────────────────

interface AppContextValue {
  state: AppState;
  dispatch: React.Dispatch<Action>;
  sendWsMessage: (payload: Record<string, unknown>) => void;
  setSendWsMessage: (fn: (payload: Record<string, unknown>) => void) => void;
  registerAckTimer: (clientMessageId: string, cancel: () => void) => void;
  setRegisterAckTimer: (fn: (clientMessageId: string, cancel: () => void) => void) => void;
  actions: {
    loadChannels: () => Promise<void>;
    loadMessages: (channelId: string) => Promise<void>;
    loadOlderMessages: (channelId: string) => Promise<void>;
    loadCurrentUser: () => Promise<void>;
    loadPermissions: () => Promise<void>;
    loadOnlineUsers: () => Promise<void>;
    selectChannel: (channelId: string) => void;
    sendMessage: (
      channelId: string,
      content: string,
      contentType?: 'text' | 'image',
    ) => Promise<Message>;
    createChannel: (
      name: string,
      topic?: string,
      memberIds?: string[],
      visibility?: 'public' | 'private',
    ) => Promise<Channel>;
    loadDmChannels: () => Promise<void>;
    openDm: (userId: string) => Promise<DmChannel>;
  };
}

const AppContext = createContext<AppContextValue | null>(null);

// __test_reducer + __test_initialState — 测试专用 export. AppProvider 里
// `useReducer(reducer, initialState)` 走非 export 的 closure; 测试 (例如
// #687 ADD_MESSAGE 三层防御 client 单测) 想直接调 reducer 不起整个 React
// 树, 走这两个 export. 反约束: production import 这俩等于绕 AppProvider
// 控制流, 不允许 — 文件名 `_test_*` 是 lint hint.
export const __test_reducer = reducer;
export const __test_initialState = initialState;

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [state, rawDispatch] = useReducer(reducer, initialState);
  const [authGeneration, setAuthGeneration] = useState(0);
  const authGenerationRef = useRef(0);
  const dispatch = useCallback<React.Dispatch<Action>>(
    (action) => {
      if (authGeneration !== authGenerationRef.current) return;
      if (action.type === 'RESET_AUTH_STATE') {
        authGenerationRef.current += 1;
        setAuthGeneration(authGenerationRef.current);
      }
      rawDispatch(action);
    },
    [authGeneration],
  );
  const stateRef = useRef(state);
  stateRef.current = state;

  const sendWsMessageRef = useRef<(payload: Record<string, unknown>) => void>(() => {});
  const registerAckTimerRef = useRef<(clientMessageId: string, cancel: () => void) => void>(
    () => {},
  );

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch({ type: 'CLEAR_EXPIRED_TYPING' });
    }, 1000);
    return () => clearInterval(interval);
  }, [dispatch]);

  const loadChannels = useCallback(async () => {
    const { channels } = await api.fetchChannels();
    dispatch({ type: 'SET_CHANNELS', channels });
  }, [dispatch]);

  const loadMessages = useCallback(
    async (channelId: string) => {
      if (stateRef.current.loadingMessages.has(channelId)) return;
      dispatch({ type: 'SET_LOADING_MESSAGES', channelId, loading: true });
      try {
        const loadedBefore = Date.now();
        const { messages, has_more } = await api.fetchMessages(channelId, { limit: 100 });
        dispatch({ type: 'SET_MESSAGES', channelId, messages, hasMore: has_more, loadedBefore });
      } finally {
        dispatch({ type: 'SET_LOADING_MESSAGES', channelId, loading: false });
      }
    },
    [dispatch],
  );

  const loadOlderMessages = useCallback(
    async (channelId: string) => {
      if (stateRef.current.loadingMessages.has(channelId)) return;
      const existing = stateRef.current.messages.get(channelId);
      if (!existing || existing.length === 0) return;

      const oldest = existing[0]!;
      dispatch({ type: 'SET_LOADING_MESSAGES', channelId, loading: true });
      try {
        const { messages, has_more } = await api.fetchMessages(channelId, {
          before: oldest.created_at,
          limit: 50,
        });
        dispatch({ type: 'PREPEND_MESSAGES', channelId, messages, hasMore: has_more });
      } finally {
        dispatch({ type: 'SET_LOADING_MESSAGES', channelId, loading: false });
      }
    },
    [dispatch],
  );

  const loadCurrentUser = useCallback(async () => {
    try {
      const user = await api.fetchMe();
      dispatch({ type: 'SET_CURRENT_USER', user });
    } catch {
      // Not authenticated - will be handled by UI
    }
  }, [dispatch]);

  const loadPermissions = useCallback(async () => {
    try {
      const data = await api.fetchMyPermissions();
      dispatch({ type: 'SET_PERMISSIONS', permissions: data.details });
    } catch {
      // Ignore
    }
  }, [dispatch]);

  const loadOnlineUsers = useCallback(async () => {
    try {
      const userIds = await api.fetchOnlineUsers();
      dispatch({ type: 'SET_ONLINE_USERS', userIds });
    } catch {
      // Ignore errors
    }
  }, [dispatch]);

  const selectChannel = useCallback(
    (channelId: string) => {
      dispatch({ type: 'SET_CURRENT_CHANNEL', channelId });
      dispatch({ type: 'CLEAR_UNREAD', channelId });
      // Mark as read on server (fire and forget)
      api.markChannelRead(channelId).catch(() => {});
    },
    [dispatch],
  );

  const sendMessageAction = useCallback(
    async (
      channelId: string,
      content: string,
      contentType: 'text' | 'image' = 'text',
    ): Promise<Message> => {
      return api.sendMessage(channelId, content, contentType);
    },
    [],
  );

  const createChannelAction = useCallback(
    async (
      name: string,
      topic?: string,
      memberIds?: string[],
      visibility?: 'public' | 'private',
    ): Promise<Channel> => {
      const guildId = stateRef.current.guildId;
      if (!guildId) throw new Error('Select a Project before creating a channel');
      const channel = await api.createChannel(guildId, name, topic, memberIds, visibility);
      dispatch({ type: 'ADD_CHANNEL', channel });
      await loadPermissions();
      return channel;
    },
    [dispatch, loadPermissions],
  );

  const loadDmChannels = useCallback(async () => {
    try {
      const channels = await api.fetchDmChannels();
      dispatch({ type: 'SET_DM_CHANNELS', channels });
    } catch {
      // Ignore errors
    }
  }, [dispatch]);

  const openDm = useCallback(
    async (userId: string): Promise<DmChannel> => {
      const dm = await api.createOrGetDm(userId);
      dispatch({ type: 'ADD_DM_CHANNEL', channel: dm });
      dispatch({ type: 'SET_CURRENT_CHANNEL', channelId: dm.id });
      dispatch({ type: 'CLEAR_UNREAD', channelId: dm.id });
      api.markChannelRead(dm.id).catch(() => {});
      return dm;
    },
    [dispatch],
  );

  const sendWsMessage = useCallback((payload: Record<string, unknown>) => {
    sendWsMessageRef.current(payload);
  }, []);

  const setSendWsMessage = useCallback((fn: (payload: Record<string, unknown>) => void) => {
    sendWsMessageRef.current = fn;
  }, []);

  const registerAckTimer = useCallback((clientMessageId: string, cancel: () => void) => {
    registerAckTimerRef.current(clientMessageId, cancel);
  }, []);

  const setRegisterAckTimer = useCallback(
    (fn: (clientMessageId: string, cancel: () => void) => void) => {
      registerAckTimerRef.current = fn;
    },
    [],
  );

  const actions = useMemo(
    () => ({
      loadChannels,
      loadMessages,
      loadOlderMessages,
      loadCurrentUser,
      loadPermissions,
      loadOnlineUsers,
      selectChannel,
      sendMessage: sendMessageAction,
      createChannel: createChannelAction,
      loadDmChannels,
      openDm,
    }),
    [
      loadChannels,
      loadMessages,
      loadOlderMessages,
      loadCurrentUser,
      loadPermissions,
      loadOnlineUsers,
      selectChannel,
      sendMessageAction,
      createChannelAction,
      loadDmChannels,
      openDm,
    ],
  );

  return (
    <AppContext.Provider
      value={{
        state,
        dispatch,
        sendWsMessage,
        setSendWsMessage,
        registerAckTimer,
        setRegisterAckTimer,
        actions,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used within AppProvider');
  return ctx;
}
